import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteXSSIConnection } from "../api";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import SidePanel from "../components/sidePanel/SidePanel";
import {
  ADD_XSSI_CONNECTION_FORM_FIELDS,
  XSSI_FILTER_COLUMNS,
  XSSI_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import TableActionButton from "../ui/TableActionButton";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformXSSIRows,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import EditXSSIConnection from "../components/xssiConnections/editXSSIConnection/EditXSSIConnection";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  BULK_UPLOAD_GENERAL_TRANSLATIONS,
  DELETE_XSSI_CONNECTION_TRANSLATIONS,
  READ_XSSI_CONNECTION_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "CONNECTION-ID"?: string | null;
  "RADIO-IPV4-ADDR"?: string | null;
  "RADIO-RFSS-ID"?: string | null;
  "RADIO-SYSTEM-ID"?: string | null;
  "RADIO-WACN-ID"?: string | null;
}

const INITIAL_STATE = {
  "CONNECTION-ID": "",
  "RADIO-IPV4-ADDR": "",
  "RADIO-RFSS-ID": "",
  "RADIO-SYSTEM-ID": "",
  "RADIO-WACN-ID": "",
};

const XSSIConnections = () => {
  const {
    token,
    scopes: { xssi_connections: xssiConnections },
    hexMode,
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const [loading, setLoading] = useState(false);
  useAppTitle("XSSI Connections- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [xssiData, setXssiData] = useState<any[]>([]);
  const navigate = useNavigate();
  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });
  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const searchFields = [
    "CONNECTION-ID",
    "RADIO-IPV4-ADDR",
    "RADIO-RFSS-ID",
    "RADIO-SYSTEM-ID",
    "RADIO-WACN-ID",
  ];

  // Instructions to display in XSSI-Connection bulk upload pannel
  const XSSI_CONNECTION_NOTES = [
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.point1,
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.point2,
    BULK_UPLOAD_GENERAL_TRANSLATIONS.Notes.hexValueNote,
  ];

  const searchConfigNew = createSearchConfig(
    searchFields,
    ADD_XSSI_CONNECTION_FORM_FIELDS
  );

  const fetchXSSIConnections = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["CONNECTION-ID"] && {
              "CONNECTION-ID": data["CONNECTION-ID"],
            }),
          ...(data &&
            data["RADIO-IPV4-ADDR"] && {
              "RADIO-IPV4-ADDR": data["RADIO-IPV4-ADDR"],
            }),
          ...(data &&
            data["RADIO-RFSS-ID"] && {
              "RADIO-RFSS-ID": data["RADIO-RFSS-ID"],
            }),
          ...(data &&
            data["RADIO-SYSTEM-ID"] && {
              "RADIO-SYSTEM-ID": data["RADIO-SYSTEM-ID"],
            }),
          ...(data &&
            data["RADIO-WACN-ID"] && {
              "RADIO-WACN-ID": data["RADIO-WACN-ID"],
            }),
        });

        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/xssi-connections?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setXssiData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchXSSIConnections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  const deleteConnection = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteXSSIConnection({
          token,
          org: selectedOrg.id,
          ref,
        });
        successAlert(DELETE_XSSI_CONNECTION_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchXSSIConnections();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_XSSI_CONNECTION_TRANSLATIONS.header}
        pageDescription={READ_XSSI_CONNECTION_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <SidePanel
              toggleFilterPanel={() => handleToggleSidePanel()}
              panelName={READ_XSSI_CONNECTION_TRANSLATIONS.editRow.panelName}
              className="sidepanel sidepanel-edit fixed z-40 h-full duration-150 bg-white"
              showDelete={xssiConnections && xssiConnections.includes("d")}
              onDelete={() => deleteConnection(selectedConnectionId)}
              deleteModalDesc={READ_XSSI_CONNECTION_TRANSLATIONS.editRow.description(
                connectionId
              )}
              panelContent={
                <div className="panel-content">
                  <EditXSSIConnection
                    org={selectedOrg.id}
                    xssiRef={selectedConnectionId}
                    refreshTable={fetchXSSIConnections}
                  />
                </div>
              }
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="XSSI Connection"
              pathName="/api/v1/app-config/xssi-connections"
              csvTemplateFile={[
                "CONNECTION-ID,RADIO-IPV4-ADDR,RADIO-RFSS-ID,RADIO-SYSTEM-ID,RADIO-WACN-ID,RADIO-RFSS-4-HEX-DIGIT,IWF-RFSS-ID,IWF-SYSTEM-ID,IWF-WACN-ID,RESPONSES-BEFORE-CONNECTED,MISSED-RESPONSES-BEFORE-DISCONNECTED,VOCODER-MODES,UNIT-MIN-RANGE,UNIT-MAX-RANGE,IWF-MIN-RANGE,IWF-MAX-RANGE,RADIO-MIN-RANGE,RADIO-MAX-RANGE,POLLING-ENABLED,POLLING-INTERVAL-CONNECTED,POLLING-INTERVAL-DISCONNECTED\n",
                ",,,,TRUE,,,,1,3,,,,,,,,TRUE,10,10",
              ]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchXSSIConnections();
              }}
              notes={XSSI_CONNECTION_NOTES}
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={XSSI_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={xssiConnections && xssiConnections.includes("c")}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/xssiconnections/new`);
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rows={transformXSSIRows(xssiData, hexMode).map((row: any) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    setConnectionId(row["CONNECTION-ID"]);
                    handleToggleSidePanel();
                  }}
                />
              ),
            }))}
            headers={XSSI_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            showBulkUploadButton={
              xssiConnections && xssiConnections.includes("c")
            }
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_XSSI_CONNECTION_TRANSLATIONS.infoAlert.heading}
          subText={READ_XSSI_CONNECTION_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default XSSIConnections;
